import { useMemo, useState } from "react";
import { FunctionSquare, Plus, Trash2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { CUSTOM_METRIC_FIELDS } from "@/config/metrics";
import {
  addCustomMetric,
  removeCustomMetric,
  validateFormula,
  inferFormat,
  type CustomMetric,
  type CustomMetricFormat,
} from "@/lib/custom-metrics";

interface CustomMetricsManagerProps {
  metrics: CustomMetric[];
  onChange: (next: CustomMetric[]) => void;
}

// How the auto-detected format reads to the user.
const FORMAT_LABEL: Record<CustomMetricFormat, string> = {
  int: "Number",
  currency: "Money",
  rate: "Rate",
  percent: "Percent",
};

const ALLOWED = new Set(CUSTOM_METRIC_FIELDS.map((f) => f.id));

/**
 * User-defined metrics: a named arithmetic formula over the row's base fields,
 * e.g. `total_revenue / unique_global`. Saved per-browser; the formula is run by
 * a hand-written parser (never eval). Once added they behave like built-in
 * columns in the Metrics picker, the grid and export.
 */
export function CustomMetricsManager({
  metrics,
  onChange,
}: CustomMetricsManagerProps) {
  const [name, setName] = useState("");
  const [formula, setFormula] = useState("");

  // Live check so Add is only enabled for a safe formula over known fields.
  const check = useMemo(
    () => (formula.trim() ? validateFormula(formula, ALLOWED) : null),
    [formula],
  );
  const canAdd = Boolean(name.trim() && check?.ok);
  // Format is derived from the formula's units, not chosen by hand.
  const format = useMemo(
    () => (check?.ok ? inferFormat(formula) : null),
    [check, formula],
  );

  // Autocomplete: the field id the user is in the middle of typing (trailing
  // identifier), and the fields that match it. Empty token → show everything.
  const token = /([a-zA-Z_][a-zA-Z0-9_]*)$/.exec(formula)?.[1] ?? "";
  const suggestions = useMemo(() => {
    if (!token) return CUSTOM_METRIC_FIELDS;
    const t = token.toLowerCase();
    return CUSTOM_METRIC_FIELDS.filter(
      (f) => f.id.toLowerCase().includes(t) || f.label.toLowerCase().includes(t),
    );
  }, [token]);

  const insertField = (id: string) =>
    setFormula((f) => {
      // Complete the trailing token if the user was typing one, else append.
      if (/[a-zA-Z_][a-zA-Z0-9_]*$/.test(f))
        return `${f.replace(/[a-zA-Z_][a-zA-Z0-9_]*$/, id)} `;
      return `${f && !/[\s(]$/.test(f) ? `${f} ` : f}${id} `;
    });

  const add = () => {
    if (!canAdd) return;
    const trimmed = formula.trim();
    const metric: CustomMetric = {
      id:
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : String(Date.now()),
      name: name.trim(),
      formula: trimmed,
      format: inferFormat(trimmed),
    };
    onChange(addCustomMetric(metrics, metric));
    setName("");
    setFormula("");
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2 cursor-pointer">
          <FunctionSquare className="h-4 w-4" />
          Custom{metrics.length ? ` (${metrics.length})` : ""}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3 p-3">
        {metrics.length > 0 && (
          <div className="max-h-40 space-y-0.5 overflow-y-auto">
            {metrics.map((m) => (
              <div
                key={m.id}
                className="flex items-center gap-1 rounded hover:bg-muted"
              >
                <div className="flex-1 truncate px-2 py-1.5">
                  <div className="truncate text-sm font-medium">{m.name}</div>
                  <code className="text-[11px] text-muted-foreground">
                    {m.formula}
                  </code>
                </div>
                <button
                  type="button"
                  onClick={() => onChange(removeCustomMetric(metrics, m.id))}
                  title="Delete this metric"
                  className="shrink-0 cursor-pointer rounded p-1 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-2 border-t border-border pt-3">
          <Input
            className="h-8"
            placeholder="Name, e.g. Revenue per visitor"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            className={cn(
              "h-8 font-mono text-xs",
              check?.ok &&
                "border-green-500 focus-visible:border-green-500 focus-visible:ring-green-500/40",
              check &&
                !check.ok &&
                "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/40",
            )}
            placeholder="Formula, e.g. total_revenue / unique_global"
            value={formula}
            onChange={(e) => setFormula(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canAdd) add();
            }}
          />
          {check && (
            <p
              className={cn(
                "text-[11px]",
                check.ok ? "text-green-600 dark:text-green-500" : "text-destructive",
              )}
            >
              {check.ok && format
                ? `✓ Valid · shows as ${FORMAT_LABEL[format]}`
                : check.ok
                  ? "✓ Valid formula"
                  : check.error}
            </p>
          )}

          {/* Fields — filtered to what the user is typing (autocomplete). Click
              to insert / complete. */}
          <div className="flex max-h-24 flex-wrap gap-1 overflow-y-auto">
            {suggestions.length === 0 ? (
              <span className="text-[11px] text-muted-foreground">
                No field matches “{token}”
              </span>
            ) : (
              suggestions.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => insertField(f.id)}
                  title={f.label}
                  className="cursor-pointer rounded border border-border bg-muted/50 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  {f.id}
                </button>
              ))
            )}
          </div>

          <Button
            size="sm"
            className="h-8 w-full"
            onClick={add}
            disabled={!canAdd}
          >
            <Plus className="size-3.5" /> Add metric
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
